import React from "react";
import {Typography, Box, Stack} from "@mui/material";
import {Price} from "@/types/search/tour-search-results";
import {ColletteChip} from "@collette-travel/collette-common-controls";
import {useTranslations} from "next-intl";

interface TourCardPricingProps {
  tourId: string;
  price: Price;
  crossedOutPrice?: Price;
  offerSavings?: string;
}

const TourCardPricing: React.FC<TourCardPricingProps> = ({
  tourId,
  price,
  crossedOutPrice,
  offerSavings
}) => {
  const t = useTranslations("SEARCH");
  return (
    <Stack
      sx={{
        flexDirection: "column",
        gap: "0.6rem"
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
          alignSelf: "stretch"
        }}
      >
        <Typography
          sx={{
            color: "text.primary",
            fontSize: "1.6rem",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "150%",
            whiteSpace: {xs: "normal", md: "nowrap"}
          }}
        >
          {t("From")}
        </Typography>

        <Typography
          sx={{
            color: "secondary.main",
            fontSize: "1.6rem",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "150%",
            whiteSpace: "nowrap",
            "&::after": { content: "\"*\"", ariaHidden: "true" }
          }}
          data-testid={`${tourId}_tour-card_price`}
        >
          {price.value} {price.unit}
        </Typography>

        {crossedOutPrice?.value && (
          <Typography
            sx={{
              color: "text.disabled",
              fontSize: "1.4rem",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "150%",
              textDecoration: "line-through",
              whiteSpace: {xs: "normal", md: "nowrap"},
              "&::after": { content: "\"*\"", ariaHidden: "true" }
            }}
            data-testid={`${tourId}_tour-card_crossed-out-price`}
          >
            {crossedOutPrice.value} {crossedOutPrice.unit}
          </Typography>
        )}
      </Box>
      <Box sx={{
        height: "3rem",
        "@container tour-search-results-grid (max-width: 63.2rem)": {
          display: offerSavings ? "block" : "none"
        }
      }}>
        {offerSavings && <ColletteChip
          color="warning"
          sx={{
            borderRadius: "1rem",
            fontWeight: 600,
            fontSize: "1.2rem"
          }}
          testId={`${tourId}_tour-card_offer-savings`}
          label={`${t("Save up to")} ${offerSavings}`}
        />}
      </Box>
    </Stack>
  );
};

export default TourCardPricing;